import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import type { AlertsHeatmapResponse } from '../../../API/types/Alerts';

type HeatmapPoint = { lat: number; lon: number };

const LEGACY_HEATMAP_COLOR_RANGE: [number, number, number, number][] = [
  [127, 255, 0, 40],
  [127, 255, 0, 100],
  [127, 255, 0, 160],
  [127, 255, 0, 210],
  [150, 255, 0, 230],
  [173, 255, 47, 245],
  [200, 255, 0, 250],
  [220, 255, 80, 255],
  [255, 220, 80, 255],
  [235, 85, 85, 255],
];

const DEFAULT_OPACITY = 0.8;
const HEATMAP_RADIUS_PX = 28;
const HEATMAP_INTENSITY = 1.7;
const HEATMAP_THRESHOLD = 0.04;
const HEATMAP_POINT_WEIGHT = 1;

function toHeatmapPoints(heatMap?: AlertsHeatmapResponse): HeatmapPoint[] {
  return (heatMap || [])
    .filter((item) => item?.lat && item?.lon)
    .map((item) => ({ lat: item.lat, lon: item.lon }));
}

function createAlertsHeatmapLayer(data: HeatmapPoint[]): HeatmapLayer<HeatmapPoint> {
  return new HeatmapLayer<HeatmapPoint>({
    id: 'alerts-heatmap',
    data,
    getPosition: (d) => [d.lon, d.lat],
    getWeight: HEATMAP_POINT_WEIGHT,
    radiusPixels: HEATMAP_RADIUS_PX,
    intensity: HEATMAP_INTENSITY,
    threshold: HEATMAP_THRESHOLD,
    opacity: DEFAULT_OPACITY,
    colorRange: LEGACY_HEATMAP_COLOR_RANGE,
    pickable: false,
  });
}

export function useAlertsHeatmapDeckGl(
  map: google.maps.Map | null,
  heatMap?: AlertsHeatmapResponse,
): void {
  const overlayRef = useRef<GoogleMapsOverlay | null>(null);
  const attachedMapRef = useRef<google.maps.Map | null>(null);
  const points = useMemo(() => toHeatmapPoints(heatMap), [heatMap]);

  const destroyOverlay = useCallback((): void => {
    if (overlayRef.current) {
      overlayRef.current.setMap(null);
      overlayRef.current.finalize();
      overlayRef.current = null;
    }
    attachedMapRef.current = null;
  }, []);

  const applyHeatmap = useCallback((): void => {
    if (!map || points.length === 0) {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
      }
      return;
    }

    const layer = createAlertsHeatmapLayer(points);

    if (attachedMapRef.current && attachedMapRef.current !== map) {
      destroyOverlay();
    }

    if (!overlayRef.current) {
      overlayRef.current = new GoogleMapsOverlay({ layers: [layer] });
      attachedMapRef.current = map;
      overlayRef.current.setMap(map);
      return;
    }

    overlayRef.current.setProps({ layers: [layer] });
    overlayRef.current.setMap(map);
    attachedMapRef.current = map;
  }, [map, points, destroyOverlay]);

  useEffect(() => {
    if (!map) {
      destroyOverlay();
      return undefined;
    }

    applyHeatmap();

    const resizeListener = google.maps.event.addListener(map, 'resize', applyHeatmap);

    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        applyHeatmap();
        google.maps.event.trigger(map, 'resize');
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', applyHeatmap);

    return () => {
      google.maps.event.removeListener(resizeListener);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', applyHeatmap);
      destroyOverlay();
    };
  }, [map, points, applyHeatmap, destroyOverlay]);
}
